/**
 * Confirmation dialog for the in-place reinstall of a **live**, Stopped VM (design doc §5a): one
 * request deletes the machine and rebuilds it on the same uuid and IP, so the row never leaves the
 * list. Refused up front (before the form) unless the VM has a real provider uuid, is within its
 * paid term and is Stopped; the form itself additionally asks for a reason and the VM's name typed
 * back, since nothing on the machine survives.
 *
 * The choice is which OS ends up on the machine: its current one (the default), or a different
 * active image from the admin OS catalog. The dialog spells out what survives (the uuid and the
 * recorded IP) and what does not (the disks, the old login, firewall rules, snapshots).
 */

import { useEffect, useId, useRef, useState } from "react";
import {
  AlertTriangle,
  Check,
  Copy,
  RefreshCw,
  RotateCcw,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";
import { HelpPopover, HelpSection } from "@/components/common/HelpPopover";
import { useAdminOSImages } from "@/hooks/useAdminOSImageQueries";
import { readWindowsLicense } from "@/services/admin-os-image.service";
import { NOTICE_HTML_CLASSES, toSafeNoticeHtml } from "@/lib/noticeHtml";
import { hasProviderVm } from "@/lib/providerVm";
import { APIError } from "@/lib/api";
import {
  DELETE_REASON_MAX_LENGTH,
  DELETE_REASON_MIN_LENGTH,
  type AdminOSImage,
} from "@/types/admin";

/**
 * What the in-place reinstall keeps, removes, and leaves for the owner to redo (design doc §1).
 * The unpaid renewal order is untouched since 2026-09-25, so it is neither kept nor removed here —
 * it never comes up. "Kept" is what WiCloud sends n8n from its own record, not what the machine
 * held: the configuration check against the machine has been behind `vm_reinstall_config_check_enabled`
 * (off) since 2026-10-13, and a non-public address is left out of the request.
 */
const IMPACT = [
  {
    title: "Kept",
    icon: Check,
    tone: "text-ok",
    items: [
      "Provider uuid and VM name",
      "IP address on WiCloud's record (sent only when it is a public address)",
      "CPU, RAM, disk size and type, and network speed, as WiCloud has them on record",
      "Paid term and expiry date",
    ],
  },
  {
    title: "Removed",
    icon: X,
    tone: "text-destructive",
    items: [
      "All data on the disks",
      "Firewall rules",
      "Snapshots",
      "The old login details (the username is root on Linux, administrator on Windows)",
    ],
  },
  {
    title: "Owner has to redo",
    icon: RotateCcw,
    tone: "text-warn",
    items: [
      "Add the firewall rules again",
      "Use the new login details on the VM page",
      "Reinstall software and restore data",
    ],
  },
] as const;

/** Which OS the reinstall installs. "current" sends no `templateId`, keeping the VM's own OS. */
type OsChoice = "current" | "different";

/** Payload handed to `onConfirm`. */
export interface ReinstallConfirmPayload {
  templateId?: string;
  confirmationName: string;
  reason: string;
}

/**
 * The in-place reinstall's own up-front refusals (design doc §5a, rows 1/3/4 — the cheap, row-only
 * checks; the provider-side rows 8–13 are the backend's to answer). `null` means the form may be
 * shown.
 */
type ReinstallGate =
  | "noMachine"
  | "expired"
  | "suspended"
  | "reinstalling"
  | "error"
  | "notStopped"
  | null;

function reinstallGate(
  resourceStatus: string | undefined,
  providerResourceId: string | null | undefined,
  expiresAt: string | null | undefined,
): ReinstallGate {
  if (!hasProviderVm(providerResourceId)) {
    return "noMachine";
  }

  // No expiry on record counts as ended, as the backend's `Resource.HasElapsedTerm` reads it: a VM
  // with no term has no term an operator may still reinstall under.
  const termElapsed =
    expiresAt == null || new Date(expiresAt).getTime() <= Date.now();
  if (termElapsed) {
    return "expired";
  }

  if (resourceStatus === "Suspended") {
    return "suspended";
  }

  // Decommissioning IS a reinstall already running (a live row never reads it any other way — see
  // `ResourceDetailPage`'s `isReinstallingLive`), so it needs its own copy rather than falling into
  // "notStopped", which would read as "This VM is Decommissioning. Shut it down first."
  if (resourceStatus === "Decommissioning") {
    return "reinstalling";
  }

  // An Error VM cannot be stopped, so "shut it down first" would be a dead end: it is retried or
  // recovered, never reinstalled.
  if (resourceStatus === "Error") {
    return "error";
  }

  if (resourceStatus !== "Stopped") {
    return "notStopped";
  }

  return null;
}

interface ReinstallResourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceName: string;
  /** The VM's current OS/template name, shown as the "keep current" option's caption. */
  currentOperatingSystem?: string | null;
  /**
   * The configuration the VM is rebuilt with — a reinstall only changes the OS. An image whose
   * minimum is above it is shown disabled, as the backend refuses it (BELOW_TEMPLATE_MINIMUM_SPECS).
   * Null means not recorded, and that axis is not measured, the same as the backend.
   */
  vmCores?: number | null;
  vmMemoryMb?: number | null;
  vmDiskGb?: number | null;
  /** Live status, provider uuid and paid-term end, for the up-front gate (`reinstallGate`). */
  resourceStatus?: string;
  providerResourceId?: string | null;
  expiresAt?: string | null;
  isPending: boolean;
  onConfirm: (payload: ReinstallConfirmPayload) => void;
  /**
   * The mutation's own error, once a submit has been made. A `TEMPLATE_*` code means the picked
   * image no longer exists, was deactivated, or has no name — all things that happened outside this
   * dialog — so it refetches the catalog and drops the selection instead of leaving the operator to
   * resubmit the same now-invalid id.
   */
  error?: unknown;
}

/** `TEMPLATE_*` codes the backend refuses a reinstall with when the picked image itself is the
 * problem, rather than anything about the VM. */
const TEMPLATE_ERROR_CODES = new Set([
  "TEMPLATE_NOT_FOUND",
  "TEMPLATE_INACTIVE",
  "TEMPLATE_NAME_MISSING",
  "BELOW_TEMPLATE_MINIMUM_SPECS",
]);

export function ReinstallResourceDialog({
  open,
  onOpenChange,
  resourceName,
  currentOperatingSystem,
  vmCores = null,
  vmMemoryMb = null,
  vmDiskGb = null,
  resourceStatus,
  providerResourceId,
  expiresAt,
  isPending,
  onConfirm,
  error,
}: ReinstallResourceDialogProps) {
  const choiceFieldId = useId();
  const templateFieldId = useId();
  const reasonFieldId = useId();
  const nameFieldId = useId();

  const [choice, setChoice] = useState<OsChoice>("current");
  const [templateId, setTemplateId] = useState<string>("");
  const [reason, setReason] = useState("");
  const [typedName, setTypedName] = useState("");
  // Set on this dialog's own submit, so an `error` left over from an earlier row's failed reinstall
  // is never read as the answer to this one — mirrors `DeleteResourceDialog`'s `submittedRef`.
  const submittedRef = useRef(false);

  // Always reopens empty: inheriting the previous row's answers — a different OS, someone else's
  // typed name — would apply them to a row the operator never looked at.
  useEffect(() => {
    if (open) {
      setChoice("current");
      setTemplateId("");
      setReason("");
      setTypedName("");
      submittedRef.current = false;
    }
  }, [open]);

  const gate = reinstallGate(resourceStatus, providerResourceId, expiresAt);
  const isBlocked = gate !== null;

  // Active images only — the same catalog the wizard offers, so a retired or disabled image cannot
  // be picked here either. The dialog is mounted only while a row is picked, so this runs on open.
  const {
    data: images,
    isLoading: imagesLoading,
    isError: imagesFailed,
    refetch: refetchImages,
  } = useAdminOSImages({
    status: "Active",
    page: 1,
    pageSize: 100,
  });

  // Runs once per failed submit: a new attempt clears the mutation's error, and its failure is a
  // new object. The picked image is the problem for these four codes — retired, deactivated, given
  // no name, or now below the VM's minimum — so the selection is dropped and the catalog refetched
  // rather than leaving the operator to resubmit the same now-invalid id.
  useEffect(() => {
    if (!submittedRef.current || !(error instanceof APIError)) {
      return;
    }

    if (!TEMPLATE_ERROR_CODES.has(error.code)) {
      return;
    }

    setTemplateId("");
    void refetchImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const selectedImage = images?.data.find((image) => image.id === templateId);
  const selectedImageLicense =
    choice === "different" && selectedImage
      ? readWindowsLicense(selectedImage.metadata)
      : null;
  // Mirrors the customer wizard and the OS image form preview: the notice appears only for a
  // Windows image marked unsupported that actually carries a message.
  const licenseNotice =
    selectedImage?.category === "Windows" &&
    selectedImageLicense?.activationSupported === false
      ? selectedImageLicense.warningMessage.trim()
      : "";

  const trimmedReason = reason.trim();
  const reasonValid =
    trimmedReason.length >= DELETE_REASON_MIN_LENGTH &&
    trimmedReason.length <= DELETE_REASON_MAX_LENGTH;
  const nameMatches = typedName.trim() === resourceName;

  const osChoiceValid =
    choice === "current" || (choice === "different" && templateId !== "");

  const canConfirm =
    !isPending && !isBlocked && osChoiceValid && reasonValid && nameMatches;

  const handleConfirm = () => {
    if (!canConfirm) {
      return;
    }

    submittedRef.current = true;
    const chosenTemplateId = choice === "different" ? templateId : undefined;
    onConfirm({
      templateId: chosenTemplateId,
      confirmationName: typedName.trim(),
      reason: trimmedReason,
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (isPending && !next) {
          return;
        }

        onOpenChange(next);
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Reinstall this VM?</DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <DialogDescription className="break-words">
            {`"${resourceName}" is erased and rebuilt in place, on the same machine.`}
          </DialogDescription>
          {gate === "noMachine" && (
            <p className="rounded-md border p-3 text-sm text-muted-foreground">
              This VM has no machine recorded at the infrastructure, so there is
              nothing to rebuild. Try "Update resource by UUID" first.
            </p>
          )}

          {gate === "expired" && (
            <p className="rounded-md border p-3 text-sm text-muted-foreground">
              This VM's paid term has ended, or it has no expiry date on record,
              so it cannot be reinstalled. Change its expiry date first, or let
              the customer renew it.
            </p>
          )}

          {gate === "suspended" && (
            <p className="rounded-md border p-3 text-sm text-muted-foreground">
              This VM is suspended, which locks it at the infrastructure — the
              infrastructure refuses to reinstall a locked machine. Resume it,
              shut it down, then reinstall it.
            </p>
          )}

          {gate === "reinstalling" && (
            <p className="rounded-md border p-3 text-sm text-muted-foreground">
              This VM is already being reinstalled.
            </p>
          )}

          {gate === "error" && (
            <p className="rounded-md border p-3 text-sm text-muted-foreground">
              This VM is in Error, so it cannot be reinstalled. Use Retry
              provisioning or Update resource by UUID instead.
            </p>
          )}

          {gate === "notStopped" && (
            <p className="rounded-md border p-3 text-sm text-muted-foreground">
              {`This VM is ${resourceStatus ?? "not stopped"}. Shut it down first, then reinstall it.`}
            </p>
          )}

          {!isBlocked && (
            <>
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Every disk on this VM is wiped and reinstalled — nothing on
                  them survives. A new root/administrator password is stored for
                  the owner's VM page when the infrastructure accepts the
                  request. The VM is usually Running again within a few minutes;
                  if it is not running after 10 minutes it goes to Error.
                </AlertDescription>
              </Alert>

              <div className="space-y-3 rounded-md border p-3 text-sm">
                {IMPACT.map((group) => (
                  <section key={group.title}>
                    <p className={`mb-1.5 font-medium ${group.tone}`}>
                      {group.title}
                    </p>
                    <ul className="space-y-1">
                      {group.items.map((item) => (
                        <li key={item} className="flex gap-1.5">
                          <group.icon
                            className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${group.tone}`}
                            aria-hidden
                          />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">Operating system</p>
                <RadioGroup
                  aria-label="Operating system"
                  value={choice}
                  onValueChange={(next) => setChoice(next as OsChoice)}
                  disabled={isPending}
                  className="gap-3"
                >
                  <label
                    htmlFor={`${choiceFieldId}-current`}
                    className="flex cursor-pointer items-start gap-3"
                  >
                    <RadioGroupItem
                      value="current"
                      id={`${choiceFieldId}-current`}
                      className="mt-0.5 cursor-pointer"
                    />
                    <span className="text-sm leading-snug">
                      <span className="font-medium">Keep the current OS</span>
                      <span className="block text-muted-foreground">
                        {currentOperatingSystem
                          ? `Reinstalls ${currentOperatingSystem} fresh.`
                          : "Reinstalls the VM's current image fresh."}
                      </span>
                    </span>
                  </label>
                  <label
                    htmlFor={`${choiceFieldId}-different`}
                    className="flex cursor-pointer items-start gap-3"
                  >
                    <RadioGroupItem
                      value="different"
                      id={`${choiceFieldId}-different`}
                      className="mt-0.5 cursor-pointer"
                    />
                    <span className="text-sm leading-snug">
                      <span className="font-medium">
                        Install a different OS image
                      </span>
                      <span className="block text-muted-foreground">
                        Picks a new image from the OS catalog for this machine.
                      </span>
                    </span>
                  </label>
                </RadioGroup>
              </div>

              {/* A failed or empty catalog would otherwise leave an empty dropdown and a disabled
                button with nothing saying why. */}
              {choice === "different" && imagesFailed && (
                <div className="flex items-center justify-between gap-2 rounded-md border border-destructive/40 p-3 text-sm text-destructive">
                  <span>Could not load the OS images.</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void refetchImages()}
                  >
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                    Retry
                  </Button>
                </div>
              )}

              {choice === "different" &&
                !imagesFailed &&
                !imagesLoading &&
                images?.data.length === 0 && (
                  <p className="rounded-md border p-3 text-sm text-muted-foreground">
                    No active OS images in the catalog. Keep the current OS, or
                    activate an image first.
                  </p>
                )}

              {choice === "different" &&
                !imagesFailed &&
                (imagesLoading || (images?.data.length ?? 0) > 0) && (
                  <div className="space-y-2">
                    <Label htmlFor={templateFieldId}>OS image</Label>
                    <Select
                      value={templateId}
                      onValueChange={setTemplateId}
                      disabled={isPending || imagesLoading}
                    >
                      <SelectTrigger
                        id={templateFieldId}
                        className="cursor-pointer"
                      >
                        <SelectValue
                          placeholder={
                            imagesLoading
                              ? "Loading images…"
                              : "Select an OS image"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {images?.data.map((image) => {
                          const shortfall = describeShortfall(
                            image,
                            vmCores,
                            vmMemoryMb,
                            vmDiskGb,
                          );
                          return (
                            <SelectItem
                              key={image.id}
                              value={image.id}
                              disabled={shortfall !== null}
                              className="cursor-pointer"
                            >
                              {image.name}
                              {image.version ? ` (${image.version})` : ""}
                              {shortfall ? ` — needs ${shortfall}` : ""}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                )}

              {licenseNotice && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription
                    className={NOTICE_HTML_CLASSES}
                    // Sanitised through the shared allowlist (`toSafeNoticeHtml`) — this is
                    // operator-authored copy from the OS image catalog, not user input.
                    dangerouslySetInnerHTML={{
                      __html: toSafeNoticeHtml(licenseNotice),
                    }}
                  />
                </Alert>
              )}

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor={reasonFieldId}>
                    Reason for the reinstall{" "}
                    <span className="text-destructive-ink" aria-hidden="true">
                      *
                    </span>
                  </Label>
                  <Textarea
                    id={reasonFieldId}
                    required
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    placeholder="Why is this VM being reinstalled?"
                    maxLength={DELETE_REASON_MAX_LENGTH}
                    rows={3}
                    disabled={isPending}
                    aria-invalid={reason.length > 0 && !reasonValid}
                  />
                  <p className="text-xs text-muted-foreground">
                    {`At least ${DELETE_REASON_MIN_LENGTH} characters, at most ${DELETE_REASON_MAX_LENGTH}. Recorded in the audit log.`}
                  </p>
                </div>

                <div className="space-y-1.5">
                  {/* Same shape as DeleteResourceDialog and the client's DeleteVMDialog: the name
                    sits in the label, its copy button beside it. */}
                  <div className="flex items-center gap-1">
                    <Label htmlFor={nameFieldId} className="min-w-0 break-all">
                      Type{" "}
                      <code className="rounded bg-panel-2 px-1 py-0.5 font-mono text-[0.8125rem] text-foreground">
                        {resourceName}
                      </code>{" "}
                      to confirm
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0 text-muted-foreground"
                      onClick={() => {
                        void navigator.clipboard.writeText(resourceName);
                        toast.success("VM name copied");
                      }}
                      title="Copy the VM name"
                      aria-label="Copy the VM name"
                      disabled={isPending}
                    >
                      <Copy className="h-3.5 w-3.5" aria-hidden />
                    </Button>
                  </div>
                  <Input
                    id={nameFieldId}
                    className="font-mono"
                    value={typedName}
                    onChange={(event) => setTypedName(event.target.value)}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    disabled={isPending}
                    aria-invalid={typedName.length > 0 && !nameMatches}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleConfirm();
                      }
                    }}
                  />
                  {typedName.length > 0 && !nameMatches && (
                    <p className="text-xs text-destructive">
                      The name you typed does not match the VM name.
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogBody>

        <DialogFooter className="gap-2">
          <ReinstallHelp />
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          {/* Destructive, like DeleteResourceDialog: this wipes every disk on the VM. */}
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {isPending && <Spinner className="mr-2" />}
            {isPending ? "Reinstalling…" : "Erase and reinstall"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The short version of `/docs/vm-actions.html#reinstall`. */
function ReinstallHelp() {
  return (
    <HelpPopover guideHref="/docs/vm-actions.html#reinstall">
      <HelpSection title="What happens">
        <p>
          Confirming only queues the work. The VM shows Reinstalling, n8n
          deletes and recreates the machine on the same uuid, and the VM turns
          itself back on as Running — usually within a few minutes.
        </p>
        <p>
          It is rebuilt from WiCloud&rsquo;s record: the OS you pick, plus the
          recorded CPU, RAM, disk, network speed and IP. If the IP on record is
          wrong, fix it with Change IP address first.
        </p>
      </HelpSection>
      <HelpSection title="If it goes wrong">
        <p>
          · n8n refuses before touching the machine: the VM is Stopped again,
          data intact
        </p>
        <p>
          · not running after 10 minutes: the VM goes to Error, possibly
          half-built — update it by UUID
        </p>
        <p>
          · no automatic retry; the infrastructure can still refuse a VM that is
          not really off, is locked by a snapshot or backup, or is missing
        </p>
      </HelpSection>
      <HelpSection title="The customer">
        <p>
          Sees the status change live and a &ldquo;reinstalled&rdquo; or
          &ldquo;failed&rdquo; entry in their activity log, and finds the new
          login on the VM page. No email. Your reason stays admin-only. It
          counts against the VM&rsquo;s reinstall limit, but an operator is
          never refused by it.
        </p>
      </HelpSection>
    </HelpPopover>
  );
}

/**
 * What the image needs beyond the VM's configuration ("2 vCPU, 4 GB RAM"), or null when it fits —
 * the admin twin of `TemplateMinimumSpecs.FindShortfalls`. A minimum of 0 or an unknown VM value is
 * not measured.
 */
function describeShortfall(
  image: AdminOSImage,
  cores: number | null,
  memoryMb: number | null,
  diskGb: number | null,
): string | null {
  const minimum = image.minimumSpecs;
  const needs: string[] = [];
  if (minimum?.cpu > 0 && cores !== null && cores < minimum.cpu) {
    needs.push(`${minimum.cpu} vCPU`);
  }
  if (minimum?.memory > 0 && memoryMb !== null && memoryMb < minimum.memory) {
    needs.push(`${minimum.memory / 1024} GB RAM`);
  }
  if (minimum?.disk > 0 && diskGb !== null && diskGb < minimum.disk) {
    needs.push(`${minimum.disk} GB disk`);
  }

  return needs.length > 0 ? needs.join(", ") : null;
}
